import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, Trophy, Play, Eye, Users, Loader2, X, Gift, AlertTriangle, Bell, CheckCircle, PauseCircle, Target, History, RefreshCw } from "lucide-react";
import { InlineCombobox } from "@/components/ui/inline-combobox";
import { formatJamaicaDate as formatDate, formatJamaicaDateTime as formatDateTime } from "@/lib/datetime";

interface CriteriaOption {
  value: string;
  label: string;
}

type CriteriaGroup = "Demographics" | "Subscription" | "Activity & Engagement" | "Progress & Performance";

interface CriteriaDefinition {
  variable: string;
  displayName: string;
  description: string;
  group: CriteriaGroup;
  dataType: "string" | "number" | "boolean" | "date" | "array";
  allowedOperators: string[];
  options?: CriteriaOption[];
}

interface CriteriaRow {
  variable: string;
  operator: string;
  value: any;
}

interface PrizeCampaign {
  id: string;
  name: string;
  description: string | null;
  status: string;
  prizeAmount: number;
  defaultMerchantId: string | null;
  defaultMerchantName: string | null;
  winnerCount: number;
  drawFrequency: string;
  lastDrawAt: string | null;
  nextDrawAt: string | null;
  scheduledAt?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  criteria?: PrizeCampaignCriteria[];
}

interface PrizeCampaignCriteria {
  id: string;
  campaignId: string;
  variable: string;
  operator: string;
  value: any;
  createdAt: string;
}

interface PrizeCampaignResult {
  id: string;
  campaignId: string;
  userId: string;
  drawId: string;
  prizeAmount: number;
  merchantId: string | null;
  merchantName: string | null;
  giftmeOrderId: string | null;
  giftmeCardCode: string | null;
  giftmeCardPin: string | null;
  status: string;
  issuedAt: string | null;
  claimedAt: string | null;
  errorMessage: string | null;
  drawnAt: string;
  user?: { firstName: string; lastName: string; email: string; username?: string };
}

interface FormData {
  name: string;
  description: string;
  status: string;
  prizeAmountDollars: string;
  defaultMerchantId: string;
  defaultMerchantName: string;
  winnerCount: string;
  drawFrequency: string;
  criteria: CriteriaRow[];
}

interface GiftmeConfig {
  id: string;
  milestone: number;
  prizeAmount: number;
  enabled: boolean;
  defaultMerchantId: string | null;
  defaultMerchantName: string | null;
  winProbability: number;
  minAvgScore: number | null;
  updatedAt: string;
}

interface MilestoneFormData {
  milestone: string;
  prizeAmountDollars: string;
  merchantId: string;
  merchantName: string;
  winProbability: string;
  minAvgScore: string;
  enabled: boolean;
}

const defaultMilestoneForm: MilestoneFormData = {
  milestone: "",
  prizeAmountDollars: "",
  merchantId: "",
  merchantName: "",
  winProbability: "25",
  minAvgScore: "",
  enabled: true,
};

const defaultFormData: FormData = {
  name: "",
  description: "",
  status: "draft",
  prizeAmountDollars: "",
  defaultMerchantId: "",
  defaultMerchantName: "",
  winnerCount: "1",
  drawFrequency: "manual",
  criteria: [],
};

const OPERATOR_LABELS: Record<string, string> = {
  equals: "is",
  not_equals: "is not",
  greater_than: "is greater than",
  less_than: "is less than",
  greater_than_or_equal: "is at least",
  less_than_or_equal: "is at most",
  in: "is any of",
  not_in: "is none of",
  between: "is between",
  contains: "includes",
};

const GROUP_ORDER: CriteriaGroup[] = [
  "Demographics",
  "Subscription",
  "Activity & Engagement",
  "Progress & Performance",
];

function statusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "active": return "default";
    case "draft": return "secondary";
    case "paused": return "outline";
    case "completed": return "secondary";
    default: return "outline";
  }
}

function resultStatusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "issued": return "default";
    case "claimed": return "default";
    case "pending": return "secondary";
    case "staged": return "outline";
    case "failed": return "destructive";
    default: return "outline";
  }
}

function formatJMD(cents: number): string {
  return `$${(cents / 100).toLocaleString()} JMD`;
}

function CriteriaBuilder({
  criteria,
  onChange,
  registry,
}: {
  criteria: CriteriaRow[];
  onChange: (criteria: CriteriaRow[]) => void;
  registry: Record<string, CriteriaDefinition>;
}) {
  const addRow = () => {
    onChange([...criteria, { variable: "", operator: "", value: "" }]);
  };

  const removeRow = (index: number) => {
    onChange(criteria.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: keyof CriteriaRow, val: any) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], [field]: val };
    if (field === "variable") {
      updated[index].operator = "";
      updated[index].value = "";
    }
    if (field === "operator") {
      updated[index].value = "";
    }
    onChange(updated);
  };

  const byGroup = GROUP_ORDER.reduce<Record<string, CriteriaDefinition[]>>((acc, g) => {
    acc[g] = Object.values(registry).filter(d => d.group === g);
    return acc;
  }, {} as Record<string, CriteriaDefinition[]>);

  const renderValueInput = (row: CriteriaRow, index: number) => {
    const def = registry[row.variable];
    if (!def || !row.operator) {
      return <p className="text-sm text-muted-foreground pt-2">Choose a field and condition first</p>;
    }

    if (row.operator === "between") {
      const val = Array.isArray(row.value) ? row.value : ["", ""];
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={val[0] ?? ""}
            onChange={(e) => updateRow(index, "value", [e.target.value ? Number(e.target.value) : "", val[1]])}
          />
          <span className="text-muted-foreground text-sm shrink-0">to</span>
          <Input
            type="number"
            placeholder="Max"
            value={val[1] ?? ""}
            onChange={(e) => updateRow(index, "value", [val[0], e.target.value ? Number(e.target.value) : ""])}
          />
        </div>
      );
    }

    if ((row.operator === "in" || row.operator === "not_in") && def.options && def.options.length > 0) {
      const selected = Array.isArray(row.value) ? row.value : [];
      return (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {def.options.map((opt) => {
            const isSelected = selected.includes(opt.value);
            return (
              <Badge
                key={opt.value}
                variant={isSelected ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => {
                  const next = isSelected
                    ? selected.filter((v: string) => v !== opt.value)
                    : [...selected, opt.value];
                  updateRow(index, "value", next);
                }}
              >
                {opt.label}
              </Badge>
            );
          })}
          {selected.length === 0 && (
            <p className="text-xs text-muted-foreground self-center">Tap to select</p>
          )}
        </div>
      );
    }

    if (def.dataType === "boolean" || (def.options && def.options.length > 0)) {
      return (
        <Select value={String(row.value ?? "")} onValueChange={(v) => updateRow(index, "value", v)}>
          <SelectTrigger>
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            {def.options!.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (def.dataType === "number") {
      return (
        <Input
          type="number"
          placeholder="Enter value"
          value={row.value ?? ""}
          onChange={(e) => updateRow(index, "value", e.target.value ? Number(e.target.value) : "")}
        />
      );
    }

    return (
      <Input
        placeholder="Enter value"
        value={row.value ?? ""}
        onChange={(e) => updateRow(index, "value", e.target.value)}
      />
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-sm font-medium">Eligibility Criteria</Label>
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <Plus className="w-3 h-3 mr-1" />
          Add Criteria
        </Button>
      </div>
      {criteria.length === 0 && (
        <p className="text-sm text-muted-foreground">No criteria added: all active students will be in the draw pool.</p>
      )}
      {criteria.map((row, index) => {
        const def = row.variable ? registry[row.variable] : null;
        const isSingleLine = def && row.operator && row.operator !== "between" &&
          row.operator !== "in" && row.operator !== "not_in";
        return (
          <div key={index} className="rounded-md border p-3 space-y-2.5">
            <div className="flex items-start gap-2">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Select value={row.variable} onValueChange={(v) => updateRow(index, "variable", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose field…" />
                  </SelectTrigger>
                  <SelectContent>
                    {GROUP_ORDER.map((group) =>
                      byGroup[group]?.length > 0 ? (
                        <div key={group}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            {group}
                          </div>
                          {byGroup[group].map((d) => (
                            <SelectItem key={d.variable} value={d.variable}>{d.displayName}</SelectItem>
                          ))}
                        </div>
                      ) : null
                    )}
                  </SelectContent>
                </Select>
                <Select
                  value={row.operator}
                  onValueChange={(v) => updateRow(index, "operator", v)}
                  disabled={!row.variable}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Condition…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(def?.allowedOperators || []).map((op) => (
                      <SelectItem key={op} value={op}>{OPERATOR_LABELS[op] || op}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="text-destructive shrink-0"
                onClick={() => removeRow(index)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            {isSingleLine ? (
              <div>{renderValueInput(row, index)}</div>
            ) : (
              renderValueInput(row, index)
            )}
            {def && (
              <p className="text-xs text-muted-foreground">{def.description}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TabInfoCard({ storageKey, icon, heading, bullets }: {
  storageKey: string;
  icon: React.ReactNode;
  heading: string;
  bullets: string[];
}) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });

  if (dismissed) return null;

  return (
    <div className="rounded-md border bg-muted/40 px-4 py-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-semibold text-sm">{heading}</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 -mt-0.5 -mr-1.5"
          onClick={() => {
            setDismissed(true);
            try { localStorage.setItem(storageKey, "1"); } catch {}
          }}
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
      <ul className="space-y-1">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-muted-foreground shrink-0" />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminPrizeCampaigns() {
  const { toast } = useToast();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<PrizeCampaign | null>(null);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const autoDescRef = useRef<string>("");
  const [previewCampaignId, setPreviewCampaignId] = useState<string | null>(null);
  const [drawCampaignId, setDrawCampaignId] = useState<string | null>(null);
  const [resultsCampaignId, setResultsCampaignId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [drawConfirmId, setDrawConfirmId] = useState<string | null>(null);
  const [revokeConfirmId, setRevokeConfirmId] = useState<string | null>(null);
  const [stagedCampaignId, setStagedCampaignId] = useState<string | null>(null);
  const [stagedWinners, setStagedWinners] = useState<PrizeCampaignResult[]>([]);

  const [isMilestoneFormOpen, setIsMilestoneFormOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<GiftmeConfig | null>(null);
  const [milestoneDeleteConfirm, setMilestoneDeleteConfirm] = useState<number | null>(null);
  const [milestoneForm, setMilestoneForm] = useState<MilestoneFormData>(defaultMilestoneForm);

  // Grant Prize state
  const [grantUserSearch, setGrantUserSearch] = useState("");
  const [grantDebouncedSearch, setGrantDebouncedSearch] = useState("");
  const [grantSelectedUser, setGrantSelectedUser] = useState<{ id: string; firstName: string; lastName: string; username?: string; phoneNumber?: string; grade?: string } | null>(null);
  const [grantShowDropdown, setGrantShowDropdown] = useState(false);
  const [grantForm, setGrantForm] = useState({ merchantId: "", merchantName: "", amountDollars: "", note: "" });

  const [grantResult, setGrantResult] = useState<{ studentName: string } | null>(null);
  const grantSearchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setGrantDebouncedSearch(grantUserSearch), 300);
    return () => clearTimeout(t);
  }, [grantUserSearch]);

  const { data: campaigns = [], isLoading } = useQuery<PrizeCampaign[]>({
    queryKey: ["/api/admin/prize-campaigns"],
  });

  const { data: registry = {} } = useQuery<Record<string, CriteriaDefinition>>({
    queryKey: ["/api/admin/prize-campaigns/registry"],
  });

  // Auto-populate campaign description based on form fields
  useEffect(() => {
    if (!isFormOpen) return;
    const { winnerCount, drawFrequency, prizeAmountDollars, defaultMerchantName, criteria } = formData;
    if (!winnerCount && !drawFrequency && !prizeAmountDollars) return;

    const freqMap: Record<string, string> = { manual: "Manual", daily: "Daily", weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly" };
    const freqLabel = freqMap[drawFrequency] || drawFrequency;
    const winNum = parseInt(winnerCount) || 1;
    const winLabel = winNum === 1 ? "1 winner" : `${winNum} winners`;
    const amount = parseFloat(prizeAmountDollars);
    const amountLabel = !isNaN(amount) && amount > 0 ? `$${amount.toLocaleString()} JMD` : null;
    const merchantLabel = defaultMerchantName || null;

    // Summarise criteria in plain English
    const critSummary = criteria
      .filter(c => c.variable && c.operator && c.value !== "" && c.value !== undefined)
      .map(c => {
        const def = registry[c.variable];
        if (!def) return null;
        const OPERATOR_SHORT: Record<string, string> = {
          equals: "=", not_equals: "≠", greater_than: ">", less_than: "<",
          greater_than_or_equal: "≥", less_than_or_equal: "≤", in: "in", not_in: "not in", between: "between", contains: "includes",
        };
        const op = OPERATOR_SHORT[c.operator] || c.operator;
        const val = Array.isArray(c.value) ? c.value.join("–") : c.value;
        return `${def.displayName} ${op} ${val}`;
      })
      .filter(Boolean);

    let desc = `${freqLabel} draw: ${winLabel}`;
    if (critSummary.length > 0) desc += ` (${critSummary.slice(0, 2).join(", ")}${critSummary.length > 2 ? ", …" : ""})`;
    if (amountLabel) desc += ` each win ${amountLabel}`;
    if (merchantLabel) desc += ` via ${merchantLabel}`;
    desc += ".";

    if (formData.description === "" || formData.description === autoDescRef.current) {
      autoDescRef.current = desc;
      setFormData(prev => ({ ...prev, description: desc }));
    }
  }, [formData.winnerCount, formData.drawFrequency, formData.prizeAmountDollars, formData.defaultMerchantName, formData.criteria, isFormOpen, registry]);

  const { data: previewData, isLoading: previewLoading } = useQuery<{ eligibleCount: number; students: any[] }>({
    queryKey: ["/api/admin/prize-campaigns", previewCampaignId, "preview"],
    enabled: !!previewCampaignId,
  });

  const { data: resultsData, isLoading: resultsLoading } = useQuery<PrizeCampaignResult[]>({
    queryKey: ["/api/admin/prize-campaigns", resultsCampaignId, "results"],
    enabled: !!resultsCampaignId,
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiRequest("POST", "/api/admin/prize-campaigns", data);
    },
    onSuccess: () => {
      toast({ title: "Campaign created successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/prize-campaigns"] });
      closeForm();
    },
    onError: (err: any) => {
      toast({ title: "Failed to create campaign", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await apiRequest("PUT", `/api/admin/prize-campaigns/${id}`, data);
    },
    onSuccess: () => {
      toast({ title: "Campaign updated successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/prize-campaigns"] });
      closeForm();
    },
    onError: (err: any) => {
      toast({ title: "Failed to update campaign", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/prize-campaigns/${id}`);
    },
    onSuccess: () => {
      toast({ title: "Campaign deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/prize-campaigns"] });
      setDeleteConfirmId(null);
    },
    onError: (err: any) => {
      toast({ title: "Failed to delete campaign", description: err.message, variant: "destructive" });
    },
  });

  const drawMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/admin/prize-campaigns/${id}/draw`);
      return res.json();
    },
    onSuccess: (data: any, id: string) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/prize-campaigns"] });
      setDrawConfirmId(null);
      if (data.winners?.length > 0) {
        setStagedWinners(data.winners);
        setStagedCampaignId(id);
        toast({ title: `${data.winners.length} winner(s) staged`, description: "Review them before sending notifications." });
      } else {
        toast({ title: "No winners selected", description: "No eligible students matched the criteria.", variant: "destructive" });
      }
    },
    onError: (err: any) => {
      toast({ title: "Draw failed", description: err.message, variant: "destructive" });
      setDrawConfirmId(null);
    },
  });

  const confirmDrawMutation = useMutation({
    mutationFn: async ({ campaignId, resultIds }: { campaignId: string; resultIds: string[] }) => {
      const res = await apiRequest("POST", `/api/admin/prize-campaigns/${campaignId}/confirm-draw`, { resultIds });
      return res.json();
    },
    onSuccess: (data: any, { campaignId }) => {
      toast({ title: "Notifications sent!", description: `${data.notified} winner(s) have been notified.` });
      setStagedCampaignId(null);
      setStagedWinners([]);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/prize-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/prize-campaigns", campaignId, "results"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to confirm draw", description: err.message, variant: "destructive" });
    },
  });

  const removeStagedMutation = useMutation({
    mutationFn: async (resultId: string) => {
      await apiRequest("DELETE", `/api/admin/prize-campaigns/staged/${resultId}`);
      return resultId;
    },
    onSuccess: (resultId: string) => {
      setStagedWinners(prev => prev.filter(w => w.id !== resultId));
      toast({ title: "Winner removed from staging" });
    },
    onError: (err: any) => {
      toast({ title: "Failed to remove winner", description: err.message, variant: "destructive" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (resultId: string) => {
      await apiRequest("DELETE", `/api/admin/prizes/campaign/${resultId}`);
    },
    onSuccess: () => {
      toast({ title: "Prize revoked" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/prize-campaigns"] });
      if (resultsCampaignId) {
        queryClient.invalidateQueries({ queryKey: ["/api/admin/prize-campaigns", resultsCampaignId, "results"] });
      }
    },
    onError: (err: any) => {
      toast({ title: "Failed to revoke prize", description: err.message, variant: "destructive" });
    },
  });

  const { data: milestones = [], isLoading: milestonesLoading } = useQuery<GiftmeConfig[]>({
    queryKey: ["/api/admin/giftme/config"],
  });

  const { data: giftmeMerchants = [], isLoading: merchantsLoading } = useQuery<Array<{ id: number; name: string; logo?: string }>>({
    queryKey: ["/api/admin/giftme/merchants"],
  });

  const { data: prizeHistory = [], isFetching: historyFetching, refetch: refetchHistory } = useQuery<Array<{
    reward: {
      id: string; userId: string; milestoneType: string; prizeAmount: number;
      merchantName: string; status: string; deliveredToPhone: string | null;
      giftmeCardCode: string | null; awardedAt: string; issuedAt: string | null; errorMessage: string | null;
    };
    user: { firstName: string; lastName: string; email: string } | null;
  }>>({
    queryKey: ["/api/admin/prizes/all-history"],
    refetchInterval: 30000,
  });

  const { data: pendingMilestones = [], isFetching: pendingFetching, refetch: refetchPending } = useQuery<Array<{
    notification: { id: string; userId: string; type: string; prizeAmount: number; milestone: number | null; createdAt: string; dismissedAt: string | null };
    user: { firstName: string; lastName: string; email: string } | null;
  }>>({
    queryKey: ["/api/admin/prizes/pending-milestones"],
    refetchInterval: 30000,
  });

  const { data: grantUserResults = [], isFetching: grantSearching } = useQuery<{ users: Array<{ id: string; firstName: string; lastName: string; username?: string; phoneNumber?: string; grade?: string }> }>({
    queryKey: ["/api/admin/users", grantDebouncedSearch],
    queryFn: async () => {
      if (!grantDebouncedSearch.trim()) return { users: [] };
      const res = await fetch(`/api/admin/users?search=${encodeURIComponent(grantDebouncedSearch)}&pageSize=10`, { credentials: "include" });
      return res.json();
    },
    enabled: grantDebouncedSearch.trim().length > 0,
  });

  const grantMutation = useMutation({
    mutationFn: async (payload: { userId: string; merchantId: string; merchantName: string; amountCents: number; note: string }) =>
      (await apiRequest("POST", "/api/admin/prizes/grant", payload)).json(),
    onSuccess: (data) => {
      setGrantResult({ studentName: data.studentName });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/prizes/all-history"] });
      toast({ title: "Prize queued!", description: `${data.studentName} will claim it from their prize page.` });
    },
    onError: (err: any) => {
      toast({ title: "Failed to grant prize", description: err.message, variant: "destructive" });
    },
  });

  const saveMilestoneMutation = useMutation({
    mutationFn: async (data: any) => apiRequest("POST", "/api/admin/giftme/config", data),
    onSuccess: () => {
      toast({ title: editingMilestone ? "Milestone updated" : "Milestone created" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/giftme/config"] });
      closeMilestoneForm();
    },
    onError: (err: any) => {
      toast({ title: "Failed to save milestone", description: err.message, variant: "destructive" });
    },
  });

  const deleteMilestoneMutation = useMutation({
    mutationFn: async (milestone: number) => apiRequest("DELETE", `/api/admin/giftme/config/${milestone}`),
    onSuccess: () => {
      toast({ title: "Milestone deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/giftme/config"] });
      setMilestoneDeleteConfirm(null);
    },
    onError: (err: any) => {
      toast({ title: "Failed to delete milestone", description: err.message, variant: "destructive" });
    },
  });

  const toggleMilestoneMutation = useMutation({
    mutationFn: async (config: GiftmeConfig) =>
      apiRequest("POST", "/api/admin/giftme/config", {
        milestone: config.milestone,
        prizeAmount: config.prizeAmount,
        enabled: !config.enabled,
        defaultMerchantId: config.defaultMerchantId,
        defaultMerchantName: config.defaultMerchantName,
        winProbability: config.winProbability ?? 100,
        minAvgScore: config.minAvgScore ?? null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/giftme/config"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to toggle milestone", description: err.message, variant: "destructive" });
    },
  });

  const closeMilestoneForm = () => {
    setIsMilestoneFormOpen(false);
    setEditingMilestone(null);
    setMilestoneForm(defaultMilestoneForm);
  };

  const openMilestoneCreate = () => {
    setMilestoneForm(defaultMilestoneForm);
    setEditingMilestone(null);
    setIsMilestoneFormOpen(true);
  };

  const openMilestoneEdit = (config: GiftmeConfig) => {
    setEditingMilestone(config);
    setMilestoneForm({
      milestone: config.milestone.toString(),
      prizeAmountDollars: (config.prizeAmount / 100).toString(),
      merchantId: config.defaultMerchantId || "",
      merchantName: config.defaultMerchantName || "",
      winProbability: (config.winProbability ?? 100).toString(),
      minAvgScore: config.minAvgScore != null ? config.minAvgScore.toString() : "",
      enabled: config.enabled,
    });
    setIsMilestoneFormOpen(true);
  };

  const handleMilestoneSubmit = () => {
    const milestoneNum = parseInt(milestoneForm.milestone);
    if (!milestoneNum || milestoneNum <= 0) {
      toast({ title: "Tests required must be a positive number", variant: "destructive" });
      return;
    }
    const prizeAmountCents = Math.round(parseFloat(milestoneForm.prizeAmountDollars || "0") * 100);
    if (prizeAmountCents <= 0) {
      toast({ title: "Prize amount must be greater than 0", variant: "destructive" });
      return;
    }
    const winProb = parseInt(milestoneForm.winProbability || "25");
    if (isNaN(winProb) || winProb < 1 || winProb > 100) {
      toast({ title: "Win probability must be between 1 and 100", variant: "destructive" });
      return;
    }
    const minScore = milestoneForm.minAvgScore.trim() ? parseInt(milestoneForm.minAvgScore) : null;
    if (minScore !== null && (isNaN(minScore) || minScore < 0 || minScore > 100)) {
      toast({ title: "Min. average score must be between 0 and 100", variant: "destructive" });
      return;
    }
    saveMilestoneMutation.mutate({
      milestone: milestoneNum,
      prizeAmount: prizeAmountCents,
      enabled: milestoneForm.enabled,
      defaultMerchantId: milestoneForm.merchantId.trim() || null,
      defaultMerchantName: milestoneForm.merchantName.trim() || null,
      winProbability: winProb,
      minAvgScore: minScore,
    });
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingCampaign(null);
    setFormData(defaultFormData);
  };

  const openCreate = () => {
    setFormData(defaultFormData);
    setEditingCampaign(null);
    setIsFormOpen(true);
  };

  const openEdit = async (campaign: PrizeCampaign) => {
    try {
      const res = await fetch(`/api/admin/prize-campaigns/${campaign.id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch campaign details");
      const full = await res.json();
      setEditingCampaign(full);
      setFormData({
        name: full.name,
        description: full.description || "",
        status: full.status,
        prizeAmountDollars: (full.prizeAmount / 100).toString(),
        defaultMerchantId: full.defaultMerchantId || "",
        defaultMerchantName: full.defaultMerchantName || "",
        winnerCount: full.winnerCount.toString(),
        drawFrequency: full.drawFrequency,
        criteria: (full.criteria || []).map((c: PrizeCampaignCriteria) => ({
          variable: c.variable,
          operator: c.operator,
          value: c.value,
        })),
      });
      setIsFormOpen(true);
    } catch {
      toast({ title: "Failed to load campaign", variant: "destructive" });
    }
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    const prizeAmountCents = Math.round(parseFloat(formData.prizeAmountDollars || "0") * 100);
    if (prizeAmountCents <= 0) {
      toast({ title: "Prize amount must be greater than 0", variant: "destructive" });
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      status: formData.status,
      prizeAmount: prizeAmountCents,
      defaultMerchantId: formData.defaultMerchantId.trim() || null,
      defaultMerchantName: formData.defaultMerchantName.trim() || null,
      winnerCount: parseInt(formData.winnerCount) || 1,
      drawFrequency: formData.drawFrequency,
      criteria: formData.criteria.filter((c) => c.variable && c.operator),
    };

    if (editingCampaign) {
      updateMutation.mutate({ id: editingCampaign.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Prize Management</h1>
        <p className="text-muted-foreground">Configure milestone prizes and manage draw campaigns.</p>
      </div>

      <Tabs defaultValue="campaigns">
        <TabsList>
          <TabsTrigger value="campaigns" className="gap-2">
            <Trophy className="w-4 h-4" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="milestones" className="gap-2">
            <Target className="w-4 h-4" />
            Milestones
          </TabsTrigger>
          <TabsTrigger value="grant" className="gap-2">
            <Gift className="w-4 h-4" />
            Grant Prize
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* ── Campaigns Tab ── */}
        <TabsContent value="campaigns" className="space-y-4 mt-4">
          <TabInfoCard
            storageKey="prize_info_campaigns"
            icon={<Trophy className="w-5 h-5 text-primary" />}
            heading="How Campaigns Work"
            bullets={[
              "A campaign defines a draw pool. All students who match your criteria are entered automatically.",
              "When a draw runs (on a schedule or manually), the system randomly selects the configured number of winners from the pool.",
              "Winners receive a GiftMe gift card delivered to their phone number. They must claim it from their Prize page.",
              "Set the status to Active and choose a frequency (e.g. Bi-weekly) for fully automatic draws, or leave it Manual to trigger draws yourself.",
            ]}
          />
          <div className="flex items-center justify-between flex-wrap gap-4">
            <p className="text-sm text-muted-foreground">Draw-based campaigns where eligible students are selected as winners.</p>
            <Button onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>

          {isLoading ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading campaigns...</span>
              </CardContent>
            </Card>
          ) : campaigns.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Trophy className="w-12 h-12 mb-4 text-muted-foreground opacity-20" />
                <p className="text-muted-foreground mb-4">No prize campaigns yet.</p>
                <Button onClick={openCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Campaign
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Prize Amount</TableHead>
                    <TableHead>Winners</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Last Draw</TableHead>
                    <TableHead>Next Draw</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <div className="font-medium">{campaign.name}</div>
                        {campaign.description && (
                          <div className="text-xs text-muted-foreground line-clamp-1">{campaign.description}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(campaign.status)} className="capitalize">
                          {campaign.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{formatJMD(campaign.prizeAmount)}</TableCell>
                      <TableCell>{campaign.winnerCount}</TableCell>
                      <TableCell>{{ manual: "Manual", daily: "Daily", weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly" }[campaign.drawFrequency] ?? campaign.drawFrequency}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {campaign.lastDrawAt
                          ? formatDate(campaign.lastDrawAt)
                          : "Never"}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {campaign.drawFrequency === "manual" ? (
                          <span className="text-muted-foreground">–</span>
                        ) : campaign.nextDrawAt ? (
                          <span className={new Date(campaign.nextDrawAt) <= new Date() ? "text-destructive font-medium" : ""}>
                            {new Date(campaign.nextDrawAt).toLocaleDateString(undefined, { timeZone: "America/Jamaica", month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Not set</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => setPreviewCampaignId(campaign.id)} title="Preview eligible students">
                            <Users className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => campaign.status === 'active' ? setDrawConfirmId(campaign.id) : undefined}
                            disabled={campaign.status !== 'active'}
                            title={campaign.status !== 'active' ? `Draw blocked: campaign is ${campaign.status}` : "Run draw (stages winners for review)"}
                          >
                            {campaign.status === 'paused' ? <PauseCircle className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => setResultsCampaignId(campaign.id)} title="View results">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(campaign)} title="Edit campaign">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteConfirmId(campaign.id)} title="Delete campaign">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* ── Milestones Tab ── */}
        <TabsContent value="milestones" className="space-y-4 mt-4">
          <TabInfoCard
            storageKey="prize_info_milestones"
            icon={<Target className="w-5 h-5 text-primary" />}
            heading="How Milestones Work"
            bullets={[
              "Each prize tier is tied to a test count, e.g. when a student completes their 10th test, they become eligible.",
              "After every qualifying test, the system rolls a random number. If it lands within the win probability (e.g. 5% = 1 in 20), the student wins.",
              "You can set a minimum average score to add an accuracy gate. Students below it are skipped even if they hit the test count.",
              "Multiple tiers can run simultaneously. A student hitting tier 1 (10 tests) does not prevent them from qualifying for tier 2 (30 tests).",
            ]}
          />
          <div className="flex items-center justify-between flex-wrap gap-4">
            <p className="text-sm text-muted-foreground">Lottery prize tiers: qualifying students have a configurable random chance to win after each test.</p>
            <Button onClick={openMilestoneCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add Prize Tier
            </Button>
          </div>

          {milestonesLoading ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading milestones...</span>
              </CardContent>
            </Card>
          ) : milestones.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Target className="w-12 h-12 mb-4 text-muted-foreground opacity-20" />
                <p className="text-muted-foreground mb-4">No milestones configured yet.</p>
                <Button onClick={openMilestoneCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Milestone
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Min. Tests</TableHead>
                    <TableHead>Prize Amount</TableHead>
                    <TableHead>Win Chance</TableHead>
                    <TableHead>Min. Score</TableHead>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...milestones].sort((a, b) => a.milestone - b.milestone).map((config) => (
                    <TableRow key={config.id}>
                      <TableCell>
                        <div className="font-medium">{config.milestone} tests</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{formatJMD(config.prizeAmount)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs whitespace-nowrap">
                          {config.winProbability ?? 100}% chance
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {config.minAvgScore ? `${config.minAvgScore}%` : <span className="italic text-xs">None</span>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {config.defaultMerchantName || <span className="italic">Not set</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={config.enabled}
                            onCheckedChange={() => toggleMilestoneMutation.mutate(config)}
                            disabled={toggleMilestoneMutation.isPending}
                          />
                          <span className="text-sm text-muted-foreground">{config.enabled ? "Active" : "Off"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openMilestoneEdit(config)} title="Edit">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setMilestoneDeleteConfirm(config.milestone)} title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* ── Grant Prize Tab ── */}
        <TabsContent value="grant" className="mt-4">
          <div className="max-w-xl mx-auto space-y-6">
            <TabInfoCard
              storageKey="prize_info_grant"
              icon={<Gift className="w-5 h-5 text-primary" />}
              heading="How Grant Prize Works"
              bullets={[
                "Use this to award a one-off GiftMe gift card to any student, no campaign or criteria needed.",
                "Search for the student by name or email, enter the prize amount and merchant, and submit.",
                "The prize is queued immediately. The student is notified and must visit their Prize page, enter their phone number, and GiftMe delivers the card.",
                "All manually granted prizes appear in the History tab.",
              ]}
            />
            <p className="text-sm text-muted-foreground">Manually issue a GiftMe gift card to any student. The card is delivered to their phone number via GiftMe.</p>

            {/* Success result card */}
            {grantResult && (
              <Card className="border-green-500/50">
                <CardContent className="pt-5 space-y-4">
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-semibold">
                    <CheckCircle className="h-5 w-5" />
                    Prize queued successfully
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{grantResult.studentName}</span> has been notified. They'll visit their prize page, enter their phone number, and GiftMe will deliver the card directly to them.
                  </p>
                  <Button variant="outline" size="sm" onClick={() => { setGrantResult(null); setGrantSelectedUser(null); setGrantUserSearch(""); setGrantForm({ merchantId: "", merchantName: "", amountDollars: "", note: "" }); }}>
                    Grant another prize
                  </Button>
                </CardContent>
              </Card>
            )}

            {!grantResult && (
              <Card>
                <CardContent className="pt-5 space-y-4">

                  {/* User search */}
                  <div className="space-y-1.5">
                    <Label>Student</Label>
                    {grantSelectedUser ? (
                      <div className="flex items-center justify-between p-3 rounded-md bg-muted/50 border">
                        <div>
                          <p className="font-medium">{grantSelectedUser.firstName} {grantSelectedUser.lastName}</p>
                          <p className="text-xs text-muted-foreground">{grantSelectedUser.username && `@${grantSelectedUser.username}`}{grantSelectedUser.grade ? ` · Grade ${grantSelectedUser.grade}` : ""}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setGrantSelectedUser(null); setGrantUserSearch(""); setGrantForm(f => ({ ...f, phone: "" })); }}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="relative" ref={grantSearchRef}>
                        <Input
                          placeholder="Search by name, email or username…"
                          value={grantUserSearch}
                          onChange={(e) => { setGrantUserSearch(e.target.value); setGrantShowDropdown(true); }}
                          onFocus={() => setGrantShowDropdown(true)}
                        />
                        {grantShowDropdown && grantDebouncedSearch.trim().length > 0 && (
                          <div className="absolute z-50 top-full mt-1 w-full rounded-md border bg-popover shadow-md max-h-56 overflow-y-auto">
                            {grantSearching && (
                              <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                              </div>
                            )}
                            {!grantSearching && (grantUserResults as any).users?.length === 0 && (
                              <p className="p-3 text-sm text-muted-foreground">No students found</p>
                            )}
                            {!grantSearching && (grantUserResults as any).users?.map((u: any) => (
                              <button
                                key={u.id}
                                className="w-full text-left px-3 py-2 text-sm hover-elevate"
                                onClick={() => {
                                  setGrantSelectedUser(u);
                                  setGrantUserSearch(`${u.firstName} ${u.lastName}`);
                                  setGrantShowDropdown(false);
                                }}
                              >
                                <span className="font-medium">{u.firstName} {u.lastName}</span>
                                {u.username && <span className="text-muted-foreground ml-2">@{u.username}</span>}
                                {u.grade && <span className="text-muted-foreground ml-2">· Grade {u.grade}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Merchant */}
                  <div className="space-y-1.5">
                    <Label>Merchant (GiftMe)</Label>
                    <InlineCombobox
                      value={grantForm.merchantId}
                      display={grantForm.merchantName}
                      onChange={(val, label) => setGrantForm(f => ({ ...f, merchantId: val, merchantName: label }))}
                      groups={[
                        { heading: "Universal", items: [{ value: "smartcard", label: "Smart Card (Any Merchant)", searchText: "smart card any merchant" }] },
                        { heading: "Specific Merchants", items: giftmeMerchants.map(m => ({ value: String(m.id), label: m.name })) },
                      ]}
                      placeholder="Select a merchant"
                      searchPlaceholder="Search merchants…"
                      loading={merchantsLoading}
                    />
                    {grantForm.merchantId === "smartcard" && (
                      <p className="text-xs text-muted-foreground">
                        The recipient can use this card at any supported GiftMe merchant.
                      </p>
                    )}
                  </div>

                  {/* Amount */}
                  <div className="space-y-1.5">
                    <Label>Amount (JMD)</Label>
                    <Input
                      type="number"
                      min="1"
                      step="0.01"
                      placeholder="e.g. 500"
                      value={grantForm.amountDollars}
                      onChange={(e) => setGrantForm(f => ({ ...f, amountDollars: e.target.value }))}
                    />
                  </div>

                  {/* Note */}
                  <div className="space-y-1.5">
                    <Label>Note (optional)</Label>
                    <Textarea
                      placeholder="e.g. Top performer this month"
                      value={grantForm.note}
                      onChange={(e) => setGrantForm(f => ({ ...f, note: e.target.value }))}
                      className="resize-none"
                      rows={2}
                    />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    The student will be notified and will enter their phone number when they claim the prize.
                  </p>

                  <Button
                    className="w-full"
                    disabled={!grantSelectedUser || !grantForm.merchantId || !grantForm.amountDollars || grantMutation.isPending}
                    onClick={() => {
                      if (!grantSelectedUser) return;
                      const amountCents = Math.round(parseFloat(grantForm.amountDollars) * 100);
                      if (!amountCents || amountCents <= 0) {
                        toast({ title: "Invalid amount", variant: "destructive" });
                        return;
                      }
                      grantMutation.mutate({
                        userId: grantSelectedUser.id,
                        merchantId: grantForm.merchantId,
                        merchantName: grantForm.merchantName,
                        amountCents,
                        note: grantForm.note,
                      });
                    }}
                  >
                    {grantMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Queuing prize…</>
                    ) : (
                      <><Gift className="h-4 w-4 mr-2" />Queue Prize for Student</>
                    )}
                  </Button>

                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ── History Tab ── */}
        <TabsContent value="history" className="mt-4">
          <TabInfoCard
            storageKey="prize_info_history"
            icon={<History className="w-5 h-5 text-primary" />}
            heading="Prize History"
            bullets={[
              "This is a complete log of every prize issued through Campaigns, Milestones, and manual Grant Prize.",
              "Status shows where each prize is in its lifecycle: Staged → Pending → Issued → Claimed (or Failed).",
              "Prizes in Staged state have been drawn but not yet confirmed. Use the Confirm button on the campaign results to issue them.",
              "You can revoke any prize that has not yet been claimed.",
            ]}
          />
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold">Prize History</h2>
              <p className="text-sm text-muted-foreground">All prizes granted, won, or issued across the platform. Auto-refreshes every 30s.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => { refetchHistory(); refetchPending(); }} disabled={historyFetching || pendingFetching}>
              <RefreshCw className={`h-4 w-4 mr-2 ${historyFetching || pendingFetching ? "animate-spin" : ""}`} />
              {historyFetching || pendingFetching ? "Refreshing…" : "Refresh"}
            </Button>
          </div>

          {/* Pending Milestone Wins: won but not yet claimed */}
          {pendingMilestones.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                <Bell className="w-4 h-4" />
                Pending Claim ({pendingMilestones.length}): Milestone won, waiting for student to claim
              </h3>
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Milestone</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Won On</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingMilestones.map(({ notification, user }) => (
                      <TableRow key={notification.id}>
                        <TableCell>
                          {user ? (
                            <div>
                              <p className="font-medium">{user.firstName} {user.lastName}</p>
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">Unknown</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs whitespace-nowrap">
                            {notification.milestone} tests completed
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm font-medium whitespace-nowrap">
                          JMD ${(notification.prizeAmount / 100).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs whitespace-nowrap border-amber-500 text-amber-600 dark:text-amber-400">
                            Pending Claim
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(notification.createdAt).toLocaleDateString('en-JM', { timeZone: "America/Jamaica", month: 'short', day: 'numeric', year: 'numeric' })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}

          {prizeHistory.length === 0 && !historyFetching ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No prize activity yet.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Delivered To</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prizeHistory.map(({ reward, user }) => (
                    <TableRow key={reward.id}>
                      <TableCell>
                        {user ? (
                          <div>
                            <p className="font-medium">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">Unknown</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs whitespace-nowrap">
                          {reward.milestoneType === 'admin_grant' ? 'Admin Grant' :
                           reward.milestoneType === 'tests_completed' ? 'Milestone' : 'Campaign'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{reward.merchantName}</TableCell>
                      <TableCell className="text-sm font-medium whitespace-nowrap">
                        JMD ${(reward.prizeAmount / 100).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-xs whitespace-nowrap ${
                            reward.status === 'issued' ? 'border-green-500 text-green-600 dark:text-green-400' :
                            reward.status === 'pending_claim' ? 'border-yellow-500 text-yellow-600 dark:text-yellow-400' :
                            reward.status === 'failed' ? 'border-red-500 text-red-600 dark:text-red-400' :
                            ''
                          }`}
                        >
                          {reward.status === 'pending_claim' ? 'Pending Claim' :
                           reward.status.charAt(0).toUpperCase() + reward.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {reward.deliveredToPhone || '–'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(reward.awardedAt).toLocaleDateString('en-JM', { timeZone: "America/Jamaica", month: 'short', day: 'numeric', year: 'numeric' })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

      </Tabs>

      <Dialog open={isFormOpen} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCampaign ? "Edit Campaign" : "Create Campaign"}</DialogTitle>
            <DialogDescription>
              {editingCampaign ? "Update the campaign details and criteria." : "Set up a new prize campaign with eligibility criteria."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Campaign Name</Label>
              <Input
                placeholder="e.g. Weekly Math Champions"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the campaign purpose..."
                value={formData.description}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData({ ...formData, description: val });
                  if (val !== autoDescRef.current) autoDescRef.current = "";
                }}
              />
              {formData.description && formData.description === autoDescRef.current && (
                <p className="text-xs text-muted-foreground italic">Auto-generated: edit to customise.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Prize Amount (JMD)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 500"
                  value={formData.prizeAmountDollars}
                  onChange={(e) => setFormData({ ...formData, prizeAmountDollars: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Merchant (GiftMe)</Label>
              <InlineCombobox
                value={formData.defaultMerchantId || "__none__"}
                display={formData.defaultMerchantName || ""}
                onChange={(val, label) => {
                  if (val === "__none__") setFormData({ ...formData, defaultMerchantId: "", defaultMerchantName: "" });
                  else setFormData({ ...formData, defaultMerchantId: val, defaultMerchantName: label });
                }}
                groups={[
                  { heading: "No Merchant", items: [{ value: "__none__", label: "None (winner chooses)", searchText: "none winner chooses" }] },
                  { heading: "Universal", items: [{ value: "smartcard", label: "Smart Card (Any Merchant)", searchText: "smart card any merchant" }] },
                  { heading: "Specific Merchants", items: giftmeMerchants.map(m => ({ value: String(m.id), label: m.name })) },
                ]}
                placeholder="Select a merchant (optional)"
                searchPlaceholder="Search merchants…"
                loading={merchantsLoading}
              />
              {formData.defaultMerchantId === "smartcard" && (
                <p className="text-xs text-muted-foreground">Winners can use this card at any supported GiftMe merchant.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Winner Count</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.winnerCount}
                  onChange={(e) => setFormData({ ...formData, winnerCount: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Draw Frequency</Label>
                <Select value={formData.drawFrequency} onValueChange={(v) => setFormData({ ...formData, drawFrequency: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t pt-4">
              <CriteriaBuilder
                criteria={formData.criteria}
                onChange={(criteria) => setFormData({ ...formData, criteria })}
                registry={registry}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingCampaign ? "Update Campaign" : "Create Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewCampaignId} onOpenChange={(open) => { if (!open) setPreviewCampaignId(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Eligible Students Preview
            </DialogTitle>
            <DialogDescription>
              Students matching the campaign criteria.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading preview...</span>
            </div>
          ) : previewData ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{previewData.eligibleCount} eligible</Badge>
              </div>
              {previewData.students && previewData.students.length > 0 ? (
                <div className="rounded-md border max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Grade</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.students.map((s: any) => (
                        <TableRow key={s.id}>
                          <TableCell className="text-sm">{s.firstName} {s.lastName}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{s.email}</TableCell>
                          <TableCell className="text-sm">G{s.grade}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">No students match the current criteria.</p>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!drawConfirmId} onOpenChange={(open) => { if (!open) setDrawConfirmId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Run Draw
            </DialogTitle>
            <DialogDescription>
              Winners will be selected and held for your review before any notifications are sent.
              You can remove individual winners from the list before confirming.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-muted px-4 py-3 text-sm text-muted-foreground">
            No notifications are sent until you review and confirm the winner list.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrawConfirmId(null)}>Cancel</Button>
            <Button
              onClick={() => { if (drawConfirmId) drawMutation.mutate(drawConfirmId); }}
              disabled={drawMutation.isPending}
            >
              {drawMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Run Draw
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stagedCampaignId && stagedWinners.length > 0} onOpenChange={(open) => { if (!open) { setStagedCampaignId(null); setStagedWinners([]); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-amber-500" />
              Review Staged Winners
            </DialogTitle>
            <DialogDescription>
              These winners have been selected but not yet notified. Remove anyone you want to exclude, then confirm to send notifications.
            </DialogDescription>
          </DialogHeader>

          {stagedWinners.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">All winners removed. Close to cancel this draw.</div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Winner</TableHead>
                    <TableHead>Prize</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stagedWinners.map((result) => (
                    <TableRow key={result.id}>
                      <TableCell>
                        <div className="text-sm font-medium">
                          {result.user ? `${result.user.firstName} ${result.user.lastName}` : result.userId}
                        </div>
                        {result.user?.email && (
                          <div className="text-xs text-muted-foreground">{result.user.email}</div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{formatJMD(result.prizeAmount)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => removeStagedMutation.mutate(result.id)}
                          disabled={removeStagedMutation.isPending}
                        >
                          <X className="w-3.5 h-3.5 mr-1" />
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                for (const w of stagedWinners) {
                  await apiRequest("DELETE", `/api/admin/prize-campaigns/staged/${w.id}`);
                }
                setStagedCampaignId(null);
                setStagedWinners([]);
                toast({ title: "Draw discarded", description: "All staged winners removed." });
              }}
            >
              Discard All
            </Button>
            <Button
              onClick={() => {
                if (stagedCampaignId) {
                  confirmDrawMutation.mutate({
                    campaignId: stagedCampaignId,
                    resultIds: stagedWinners.map(w => w.id),
                  });
                }
              }}
              disabled={confirmDrawMutation.isPending || stagedWinners.length === 0}
            >
              {confirmDrawMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Bell className="w-4 h-4 mr-2" />
              Confirm &amp; Notify {stagedWinners.length} Winner{stagedWinners.length !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resultsCampaignId} onOpenChange={(open) => { if (!open) setResultsCampaignId(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Draw Results
            </DialogTitle>
            <DialogDescription>
              Past draw results for this campaign.
            </DialogDescription>
          </DialogHeader>
          {resultsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading results...</span>
            </div>
          ) : resultsData && resultsData.length > 0 ? (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Winner</TableHead>
                    <TableHead>Prize</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Drawn At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resultsData.map((result) => (
                    <TableRow key={result.id}>
                      <TableCell>
                        <div className="text-sm font-medium">
                          {result.user
                            ? `${result.user.firstName} ${result.user.lastName}`
                            : result.userId}
                        </div>
                        {result.user?.email && (
                          <div className="text-xs text-muted-foreground">{result.user.email}</div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{formatJMD(result.prizeAmount)}</TableCell>
                      <TableCell>
                        <Badge variant={resultStatusVariant(result.status)} className="capitalize">
                          {result.status}
                        </Badge>
                        {result.errorMessage && (
                          <div className="text-xs text-destructive mt-1">{result.errorMessage}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDateTime(result.drawnAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => { setRevokeConfirmId(result.id); }}
                          disabled={revokeMutation.isPending}
                        >
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">No draw results yet for this campaign.</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete Campaign
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this campaign? This will remove all associated criteria and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => { if (deleteConfirmId) deleteMutation.mutate(deleteConfirmId); }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revokeConfirmId} onOpenChange={(open) => { if (!open) setRevokeConfirmId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Prize</DialogTitle>
            <DialogDescription>Are you sure you want to revoke this prize? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeConfirmId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => { if (revokeConfirmId) revokeMutation.mutate(revokeConfirmId); setRevokeConfirmId(null); }}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Milestone Create/Edit Dialog ── */}
      <Dialog open={isMilestoneFormOpen} onOpenChange={(open) => { if (!open) closeMilestoneForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingMilestone ? "Edit Prize Tier" : "Add Prize Tier"}</DialogTitle>
            <DialogDescription>
              {editingMilestone
                ? "Update this prize tier's settings."
                : "Create a lottery prize tier. Students who qualify will have a random chance to win after each test."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Min. Tests Required</Label>
              <Input
                type="number"
                min="1"
                placeholder="e.g. 30"
                value={milestoneForm.milestone}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, milestone: e.target.value })}
                disabled={!!editingMilestone}
              />
              {editingMilestone ? (
                <p className="text-xs text-muted-foreground">Cannot change after creation.</p>
              ) : (
                <p className="text-xs text-muted-foreground">Student must have completed at least this many tests to qualify.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Prize Amount (JMD)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 2000"
                value={milestoneForm.prizeAmountDollars}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, prizeAmountDollars: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Win Probability %</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  placeholder="e.g. 25"
                  value={milestoneForm.winProbability}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, winProbability: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">1–100. e.g. 25 = 1 in 4 chance per qualifying test.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Min. Avg Score % <span className="text-muted-foreground">(optional)</span></Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="e.g. 60"
                  value={milestoneForm.minAvgScore}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, minAvgScore: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Leave blank for no accuracy requirement.</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Merchant (GiftMe)</Label>
              <InlineCombobox
                value={milestoneForm.merchantId || "__none__"}
                display={milestoneForm.merchantName}
                onChange={(val, label) => {
                  if (val === "__none__") setMilestoneForm({ ...milestoneForm, merchantId: "", merchantName: "" });
                  else setMilestoneForm({ ...milestoneForm, merchantId: val, merchantName: label });
                }}
                groups={[
                  { heading: "No Merchant", items: [{ value: "__none__", label: "None (winner chooses)", searchText: "none winner chooses" }] },
                  { heading: "Universal", items: [{ value: "smartcard", label: "Smart Card (Any Merchant)", searchText: "smart card any merchant" }] },
                  { heading: "Specific Merchants", items: giftmeMerchants.map(m => ({ value: String(m.id), label: m.name })) },
                ]}
                placeholder="Select a merchant (optional)"
                searchPlaceholder="Search merchants…"
                loading={merchantsLoading}
              />
              {milestoneForm.merchantId === "smartcard" && (
                <p className="text-xs text-muted-foreground">Winners can use this card at any supported GiftMe merchant.</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={milestoneForm.enabled}
                onCheckedChange={(checked) => setMilestoneForm({ ...milestoneForm, enabled: checked })}
              />
              <Label>{milestoneForm.enabled ? "Active: lottery is running" : "Disabled: no prizes awarded"}</Label>
            </div>
            {/* Live preview */}
            {milestoneForm.milestone && milestoneForm.prizeAmountDollars && (() => {
              const tests = parseInt(milestoneForm.milestone);
              const amount = parseFloat(milestoneForm.prizeAmountDollars);
              const prob = parseInt(milestoneForm.winProbability) || 25;
              const minScore = milestoneForm.minAvgScore.trim() ? parseInt(milestoneForm.minAvgScore) : null;
              const merchant = milestoneForm.merchantName || null;
              if (isNaN(tests) || isNaN(amount)) return null;
              const probText = prob === 100 ? "guaranteed" : `${prob}% chance (1 in ${Math.round(100 / prob)})`;
              const merchantText = merchant ? ` via ${merchant}` : "";
              const scoreText = minScore ? ` Requires an average score of at least ${minScore}%.` : "";
              const enabledText = !milestoneForm.enabled ? " (this tier is currently disabled)" : "";
              return (
                <div className="rounded-md bg-muted/60 px-3 py-2.5 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Preview: </span>
                  When a student completes their {tests}{tests === 1 ? "st" : tests === 2 ? "nd" : tests === 3 ? "rd" : "th"} test, they have a <span className="text-foreground font-medium">{probText}</span> of winning a <span className="text-foreground font-medium">${amount.toLocaleString()} JMD</span> prize{merchantText}.{scoreText}{enabledText}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeMilestoneForm}>Cancel</Button>
            <Button onClick={handleMilestoneSubmit} disabled={saveMilestoneMutation.isPending}>
              {saveMilestoneMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingMilestone ? "Save Changes" : "Create Milestone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Milestone Delete Confirm ── */}
      <Dialog open={milestoneDeleteConfirm !== null} onOpenChange={(open) => { if (!open) setMilestoneDeleteConfirm(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete Milestone
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the <strong>{milestoneDeleteConfirm}-test</strong> milestone? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMilestoneDeleteConfirm(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => { if (milestoneDeleteConfirm !== null) deleteMilestoneMutation.mutate(milestoneDeleteConfirm); }}
              disabled={deleteMilestoneMutation.isPending}
            >
              {deleteMilestoneMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}